
namespace Roodik.Game
{
	import IGameResources = Roodik.Core.IGameResources;
	import IAnalytics = Roodik.Core.IAnalytics;
	import ViewBase = Roodik.Core.ViewBase;
	import Slot = dragonBones.Slot;
	import Tween = Roodik.Core.Tween.Tween;
	import Easing = Roodik.Core.Tween.Easing;
	import Point = PIXI.Point;
	import IStorageManager = Roodik.Core.IStorageManager;
	import ISoundManager = Roodik.Core.ISoundManager;
	
	export class GamePanel extends ViewBase
	{
		public static readonly ASSETS_LOADING:string = "GamePanel.ASSETS_LOADING";
		public static readonly ASSETS_LOADED:string = "GamePanel.ASSETS_LOADED";
		public static readonly GAME_OVER: string = "GamePanel.GameOver";
		public static readonly LEVEL_COMPLETE: string = "GamePanel.LevelComplete";
		public static readonly GAME_COMPLETE: string = "GamePanel.GameComplete";
		public static readonly SHOW_MENU: string = "GamePanel.ShowMenu";
		public static readonly ASKED_FOR_HELP: string = "GamePanel.ASKED_FOR_HELP";
		public static readonly TIMES_UP: string = "GamePanel.TIMES_UP";
		public static readonly HAS_NO_MOVES: string = "GamePanel.HAS_NO_MOVES";

		public events = new PIXI.utils.EventEmitter;
		private _scoreLabel : PIXI.Text;
		private _chainLabel : PIXI.Text;
		private _logo : SimpleButton;
		private _gameModel:GameModel;
		private _awardsModel:AwardsModel;
		private _paused:boolean;
		private _boardView : BoardView;
		private _menuBtn:SimpleButton;
		private _timerView:TimerView;
		private _reshuffleBtn:SimpleButton;
		private _tilePreview:Slot;
		private _defaultPreviewY:number = 0;
		private _previewShowed:boolean = false;
		private _overTile:TilePreview;
		private _selectedTile:TilePreview;
		private _overIndex:number = 0;
		private _reshuffleCount:number = 1;
		private _tutorialView:TutorialView;

		constructor(private _storageManager:IStorageManager,
		            private _resources:IGameResources,
		            private _soundManager:ISoundManager,
		            private _analytics:IAnalytics)
		{
			super("GamePanel");
			this.events = new PIXI.utils.EventEmitter();

			this._awardsModel = new AwardsModel(this._soundManager);

			this.createScore();
			this.createChain();
			this.createLogo();
			this.createMenuButton();
			this.createShuffleButton();
			this.createTilePreview();
			this.createTimer();
			this.createGameModel();
			this.createTutorial();
		}

		private createTutorial()
		{
			this._tutorialView = new TutorialView(this._soundManager, this._storageManager);
			this._tutorialView.x = -358;
			this._tutorialView.y = 300;
			this.addChild(this._tutorialView);
		}

		public initialize(withTimer:boolean = true)
		{
			this._gameModel.initialize((boardModel:BoardModel)=>
			{
				this._gameModel.pause();

				this._boardView = new BoardView(
				boardModel,
				withTimer,
				this.onOverTile.bind(this),
				this.onOutTile.bind(this),
				this.onSelectedTile.bind(this),
				this.tilesMatched.bind(this),
				this.wrongMatch.bind(this),
				this._awardsModel,
				this._soundManager);

				this.addChild(this._boardView);

				if (withTimer)
				{
					this._timerView.visible = true;
					this._timerView.show(this._gameModel.timesLeft);
				}
				else
				{
					this._boardView.showTiles();
				}

				this.events.emit(GamePanel.ASSETS_LOADED);
			});
			this.events.emit(GamePanel.ASSETS_LOADING);
		}

		private createGameModel()
		{
			this._gameModel = new GameModel(this._resources,(result:number, timesLeft:number)=>
			{
				switch (result)
				{
					case BoardModel.HAS_NO_MOVES:
						this.events.emit(GamePanel.HAS_NO_MOVES);
						break;
					case BoardModel.HAS_NO_TILES:
						this.events.emit(GamePanel.LEVEL_COMPLETE);
						break;
					case BoardModel.GAME_COMPLETE:
						this._awardsModel.addBonus(timesLeft);
						this.updateScore(this._awardsModel.score);
						this.events.emit(GamePanel.GAME_COMPLETE);
						break;
					case BoardModel.TIMES_UP:
						this.events.emit(GamePanel.TIMES_UP);
						break;
				}
			},()=>
			{
				this._timerView.updateTime(this._gameModel.timesLeft);
			});
			this._timerView.visible = false;
		}

		private createTimer()
		{
			this._timerView = new TimerView(this._soundManager,()=>{
				this._boardView.showTiles();
				this._gameModel.timeReady();
				this._timeReady = true;
			});
			this.addChild(this._timerView);
		}

		private _timeReady:boolean = false;

		private createScore()
		{
			this._scoreLabel = new PIXI.Text(this._awardsModel.score.toLocaleString(),
			{
				fontFamily: 'WellWait',
				fontSize: 43,
				fontStyle: 'italic',
				fontWeight: 'bold',
				fill: "#FFDEAD",
				stroke: '#000000',
				strokeThickness: 2,

				dropShadow: true,
				dropShadowColor: '#000000',
				dropShadowBlur: 1,
				dropShadowAngle: Math.PI / 4,
				dropShadowDistance: 1,

				padding:20
			});
			this._scoreLabel.x = -500;
			this._scoreLabel.y = -458;
			this.addChild(this._scoreLabel);
		}

		private createChain()
		{
			this._chainLabel = new PIXI.Text((this._awardsModel.chain+1).toLocaleString() + " x ",
			{
				fontFamily: 'WellWait',
				fontSize: 45,
				fontStyle: 'italic',
				fontWeight: 'bold',
				fill: ['#FFA500', '#FFD700'],
				stroke: '#ffffff',
				strokeThickness: 5,

				dropShadow: true,
				dropShadowColor: '#000000',
				dropShadowBlur: 3,
				dropShadowAngle: Math.PI / 4,
				dropShadowDistance: 1,

				padding:20
			});
			this._chainLabel.x = 413;
			this._chainLabel.y = -365;
			this.addChild(this._chainLabel);
		}

		private updateChain(value:number)
		{
			this._chainLabel.text = value.toLocaleString() + " x ";
		}

		private updateScore(value:number)
		{
			this._scoreLabel.text = value.toLocaleString();
		}

		private createLogo()
		{
			this._logo = new SimpleButton(this.childByName("LogoBtn").display);
			this._logo.events.on(SimpleButton.CLICKED, this.onClickLogo.bind(this));
		}

		private createMenuButton()
		{
			this._menuBtn = new SimpleButton(this.childByName("MenuButton").display);
			this._menuBtn.events.on(SimpleButton.CLICKED, this.showMenu.bind(this));
		}

		private createShuffleButton()
		{
			this._reshuffleBtn = new SimpleButton(this.childByName("ReshuffleButton").display, this._reshuffleCount.toString(),
			{
				fontFamily: 'WellWait',
				fontSize: 40,
				fill: '#ffffff',//['#ffffff', '#4a1850'], // gradient
				stroke: '#000000',
				strokeThickness: 1,
				align:"center",
				dropShadow: true,
				dropShadowColor: '#000000',
				dropShadowBlur: 2,
				dropShadowAngle: Math.PI / 4,
				dropShadowDistance: 1,

				padding:20
			});
			this._reshuffleBtn.textPosition = new Point(0,-5);
			this._reshuffleBtn.events.on(SimpleButton.CLICKED, this.clickReshuffle.bind(this));
			this.updateReshuffleButton();
		}

		private createTilePreview()
		{
			let rectangle = new PIXI.Graphics();
			rectangle.beginFill(0x000000, 1)
			rectangle.drawRect(-600,-392,250,140);
			rectangle.endFill();
			this.addChild(rectangle);

			this._tilePreview = this.childByName("tilePreview");
			this._tilePreview.display.mask = rectangle;
			this._defaultPreviewY = this._tilePreview.display.y;

			/*this._timerForPreview = new Timer(()=>{
				if (this._visibleTime != this._timerForPreview.seconds && this._overTile == null && this._selectedTile == null)
				{
					this.hideTilePreview();
				}
			});
			this._timerForPreview.stop();*/
		}

		private clickReshuffle()
		{
			if (!this._timeReady)
			{
				return;
			}

			this._soundManager.playSound("click.mp3", 1);
			if (this._reshuffleCount == 0)
			{
				this._tutorialView.doNotShowSkeleton();
				this.showSponsorRequest();
				return;
			}

			this._soundManager.playSound("reshuffle.wav", 1);

			this._awardsModel.resetChain();
			this.updateChain(this._awardsModel.chain+1);

			this._reshuffleCount--;
			this._reshuffleCount = this._reshuffleCount<0 ? 0 : this._reshuffleCount;

			let selectedModel = this._boardView.selectedTile();
			if (selectedModel != null)
			{
				selectedModel.selected = false;
			}
			this.updatePreview();
			this.onOutTile(null);
			this._gameModel.reshuffle();
			this.updateReshuffleButton();
		}

		private updateReshuffleButton()
		{
			if (this._reshuffleCount == 0)
			{
				this._tutorialView.run();
				this.childByName("ReshuffleButton").childArmature.getSlot("reshuffle_off").visible = true;
			}
			else
			{
				this.childByName("ReshuffleButton").childArmature.getSlot("reshuffle_off").visible = false;
			}

			this._reshuffleBtn.updateText(this._reshuffleCount.toString());
		}

		private onOutTile(tile:TileModel)
		{
			this._overIndex--;
			if (this._overIndex <= 0 && this._selectedTile == null)
			{
				this._overIndex = 0;
				this._tilePreview.display.removeChild(this._overTile);
				this._overTile = null;

				/*this._visibleTime = this._timerForPreview.seconds;
				this._timerForPreview.start();*/
			}
			else if (this._selectedTile != null && this._overIndex <= 0)
			{
				this._overIndex = 0;
				this._tilePreview.display.removeChild(this._overTile);
				this._overTile = null;
			}
		}

		private onOverTile(tile:TileModel)
		{
			this._overIndex++;

			if (this._overTile != null)
			{
				this._tilePreview.display.removeChild(this._overTile);
			}

			this._overTile = new TilePreview(tile);
			this._overTile.x = this._selectedTile == null ? 75 : 175;
			this._overTile.y = 55;
			this._overTile.scale.x = 0.8;
			this._overTile.scale.y = 0.8;
			this._tilePreview.display.addChild(this._overTile);

			this.showTilePreview();
		}

		private onSelectedTile(tile:TileModel)
		{
			this._soundManager.playSound("tileClick.mp3", 1);
			this.showTilePreview();

			this.updatePreview();

			this._overIndex--;
			this._selectedTile = new TilePreview(this._boardView.selectedTile());
			this._selectedTile.scale.x = 0.8;
			this._selectedTile.scale.y = 0.8;
			this._selectedTile.x = 75;
			this._selectedTile.y = 55;
			// this._tilePreview.display.removeChild(this._overTile);
			this._tilePreview.display.addChild(this._selectedTile);

			this._overTile = null;
		}

		private wrongMatch()
		{
			this._soundManager.playSound("wrongmatch.wav", 0.2);
			this._awardsModel.resetChain();
			this.updateChain(this._awardsModel.chain+1);
		}

		private tilesMatched()
		{
			this._soundManager.playSound("match.mp3", 1);
			this._soundManager.playSound("tileClick.mp3", 1);

			this._tutorialView.reset();
			this.updateChain(this._awardsModel.chain+1);
			this._awardsModel.increaseChain();
			this._awardsModel.addScore();

			this.updateScore(this._awardsModel.score);

			if (this._awardsModel.chain == 10)
			{
				this.updateChain(this._awardsModel.chain+1);
			}

			this.updatePreview();
		}

		private updatePreview()
		{
			if (this._overTile != null)
			{
				this._tilePreview.display.removeChild(this._overTile);
			}
			if (this._selectedTile != null)
			{
				this._tilePreview.display.removeChild(this._selectedTile);
			}
			this._overIndex = 0;
			this._overTile = null;
			this._selectedTile = null;
		}

		private onClickLogo()
		{
			window.open("https://info4kid.ru/games", "_blank");
		}

		private showTilePreview()
		{
			if (!this._previewShowed)
			{
				this._previewShowed = true;
				let obj={y:this._defaultPreviewY}
				new Tween(obj)
				.to({y: -390}, 200)
				.easing(Easing.Quadratic.In)
				.onUpdate(()=>{
					this._tilePreview.display.y = obj.y;
				})
				.start();
			}
		}

		private hideTilePreview()
		{
			if (this._previewShowed)
			{
				this._previewShowed = false;
				let obj={y:-390}
				new Tween(obj)
				.to({y: this._defaultPreviewY}, 200)
				.easing(Easing.Quadratic.In)
				.onUpdate(()=>{
					this._tilePreview.display.y = obj.y;
				})
				.start();
			}
		}

		public addShuffle()
		{
			this._reshuffleCount++;
			this.updateReshuffleButton();
		}

		public nextLevel()
		{
			this._boardView.destroy();
			this.removeChild(this._boardView);
			this._boardView = null;
			this.initialize(false);
		}
		
		private runLevel()
		{
		
		}

		public update()
		{
		
		}

		private showMenu()
		{
			if (!this._timeReady)
			{
				return;
			}

			this.events.emit(GamePanel.SHOW_MENU);
		}

		private showSponsorRequest()
		{
			this._tutorialView.hide();
			this.events.emit(GamePanel.ASKED_FOR_HELP);
		}

		private gameOver()
		{
			this.events.emit(GamePanel.GAME_OVER);
		}

		public totalScore():number {return this._awardsModel.score;}
		public timeBonus():number {return this._awardsModel.timeBonus;}
		public chainBonus():number {return this._awardsModel.chainBonus;}
		public maxLevels():number {return this._gameModel.maxLevels;}

		public set paused(value:boolean)
		{
			this._paused = value;
			value ? this._gameModel.pause() : this._gameModel.unPause();
		}

		public destroy()
		{
			if (this._boardView != null)
			{
				this._boardView.destroy();
				this.removeChild(this._boardView);
				this._boardView = null;
			}

			this.gameOver();

			this.removeChild(this._tutorialView);
			this.removeChild(this._chainLabel);
			this.removeChild(this._scoreLabel);
			this.removeChild(this._timerView);
			this._chainLabel.destroy();
			this._timerView.destroy();
			this._scoreLabel.destroy();
			this._tutorialView.destroy();

			this._gameModel.destroy();
			this._gameModel = null;

			this._awardsModel = null;

			this._logo.events.off(SimpleButton.CLICKED, this.onClickLogo.bind(this));
			this._logo.destroy();

			this._reshuffleBtn.events.off(SimpleButton.CLICKED, this.clickReshuffle.bind(this));
			this._reshuffleBtn.destroy();

			this._menuBtn.events.off(SimpleButton.CLICKED, this.showMenu.bind(this));
			this._menuBtn.destroy();

			this.events.removeAllListeners();
			this.events = null;

			super.destroy();
		}

	}
}


// WEBPACK FOOTER //
// /source/src/typescript/Game/View/GamePanel.ts